/* eslint-disable no-console */
import axios from 'axios';

import {CLIMATE_DB_BASE_URL, CLIMATE_DB_ENDPOINT_JSON} from '../../../config';
import CRUDMethods from '../../../constants/CRUDConstants';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import benchmark from '../DASHBOARD/benchmark';

import {ensureAIToken} from './aiTokenAPI';

type IndustryBenchmarkGroupKey = 'emissions' | 'intensity' | 'consumption';

interface IndustryBenchmarkKpi {
  dataId?: string;
  label?: string;
  value?: number | string;
  benchmark?: number | string;
  unit?: string;
}

interface IndustryBenchmarkGroup {
  key?: string;
  kpis?: IndustryBenchmarkKpi[];
}

interface IndustryBenchmarkOverview {
  groups?: IndustryBenchmarkGroup[];
}

const PLATFORM_GROUP_ENDPOINT: Record<
  IndustryBenchmarkGroupKey,
  'emissionsScopeWise' | 'intensityWise' | 'consumptionWise'
> = {
  emissions: 'emissionsScopeWise',
  intensity: 'intensityWise',
  consumption: 'consumptionWise'
};

const INDUSTRY_BENCHMARK_GROUP_KEYS: IndustryBenchmarkGroupKey[] = [
  'emissions',
  'intensity',
  'consumption'
];

const toNumber = (value: unknown): number => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
};

const groupKpis = (
  overview: IndustryBenchmarkOverview | null | undefined,
  key: IndustryBenchmarkGroupKey
): IndustryBenchmarkKpi[] | undefined =>
  overview?.groups?.find((group) => group?.key === key)?.kpis;

const mergeCompanyKpisWithBenchmarks = (
  companyKpis: IndustryBenchmarkKpi[] | undefined,
  climateKpis: IndustryBenchmarkKpi[] | undefined
): IndustryBenchmarkKpi[] | undefined => {
  if (!companyKpis?.length && !climateKpis?.length) {
    return undefined;
  }

  const climateByDataId = new Map(
    (climateKpis ?? [])
      .filter((kpi) => kpi?.dataId)
      .map((kpi) => [kpi.dataId as string, kpi])
  );

  if (companyKpis?.length) {
    return companyKpis.map((kpi) => {
      const dataId = kpi?.dataId ?? '';
      const climateKpi = climateByDataId.get(dataId);

      return {
        ...climateKpi,
        ...kpi,
        dataId,
        label: kpi.label ?? climateKpi?.label,
        unit: kpi.unit ?? climateKpi?.unit,
        value: toNumber(kpi.value),
        benchmark: toNumber(climateKpi?.benchmark)
      };
    });
  }

  return climateKpis?.map((kpi) => ({
    ...kpi,
    value: 0,
    benchmark: toNumber(kpi.benchmark)
  }));
};

const buildMergedOverview = (
  climateOverview: IndustryBenchmarkOverview | null,
  companyByGroup: Partial<
    Record<IndustryBenchmarkGroupKey, IndustryBenchmarkKpi[] | undefined>
  >
): IndustryBenchmarkOverview | null => {
  const groups = INDUSTRY_BENCHMARK_GROUP_KEYS.flatMap((key) => {
    const kpis = mergeCompanyKpisWithBenchmarks(
      companyByGroup[key],
      groupKpis(climateOverview, key)
    );

    return kpis?.length ? [{key, kpis}] : [];
  });

  return groups.length ? {groups} : null;
};

/**
 * Climate DB sector benchmarks only (`GET /api/overview`).
 * Company KPI values are not taken from this response; use
 * {@link getIndustryBenchmarkOverview}
 */
export const getIndustryBenchmark = async (year: string): Promise<any> => {
  const mongoCompanyId = useCompanyStore.getState().companyId?.trim() ?? '';
  const selectedYear = year?.trim() ?? '';
  const token = await ensureAIToken();

  if (!mongoCompanyId || !selectedYear || !token) {
    return null;
  }

  try {
    const response = await axios.request({
      method: CRUDMethods.GET,
      url: `${CLIMATE_DB_BASE_URL}${CLIMATE_DB_ENDPOINT_JSON.overview}`,
      params: {
        year: selectedYear,
        mongo_company_id: mongoCompanyId
      },
      headers: {Authorization: `Bearer ${token}`}
    });

    return response.data;
  } catch (err) {
    console.log('climate db industry benchmark err:', err);

    return null;
  }
};

/**
 * Industry benchmarks vs company for dashboard overview.
 * Sector `benchmark` values come from Climate DB; company `value` (and KPI
 * metadata) come from platform SDG endpoints.
 */
export const getIndustryBenchmarkOverview = async (
  year: string
): Promise<IndustryBenchmarkOverview | null> => {
  const selectedYear = year?.trim() ?? '';

  if (!selectedYear) {
    return null;
  }

  const [
    climateOverview,
    emissionsResponse,
    intensityResponse,
    consumptionResponse
  ] = await Promise.all([
    getIndustryBenchmark(selectedYear),
    benchmark(PLATFORM_GROUP_ENDPOINT.emissions, selectedYear),
    benchmark(PLATFORM_GROUP_ENDPOINT.intensity, selectedYear),
    benchmark(PLATFORM_GROUP_ENDPOINT.consumption, selectedYear)
  ]);

  return buildMergedOverview(climateOverview, {
    emissions: emissionsResponse?.data,
    intensity: intensityResponse?.data,
    consumption: consumptionResponse?.data
  });
};
